<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, nextTick } from 'vue';
import type { TaskLoaderState } from '@/composeables/loaders';
import CollapseContent from './Basic/BootstrapWrappers/CollapseContent.vue';
import { HelpIcon, IconComp, ButtonComp, DisplayMimeType, DisplayFilename } from '@/components';
import { featureIcons } from './Menu/featureicons';
import signalrSubscribe from '@/objects/generated/signalr/signalrSubscribe';
import { useUserStoreRef } from '@/stores';
import type { FileArchiveItem, StatusLoaderStatus } from '@/objects/generated';
import { $td, $ti } from '@/locize';
import { secondsToJsDate } from '@/utilities/dateutils';

const starting = ref<boolean>(false);

const { user } = useUserStoreRef();

const props = withDefaults(
    defineProps<{
        state?: TaskLoaderState | null;
        /**
         * The label to show in the loader
         * @default ''
         */
        label?: string;

        /**
         * If the language is not loaded yet
         * @default false
         */
        langNotLoaded?: boolean;

        /**
         * If the loader should be shown as a modal
         * @default false
         */
        showAsModal?: boolean;

        /**
         * If the details should be hidden
         * @default false
         */
        hideDetails?: boolean;

        /**
         * If the user should confirm before closing the loader. This keep the loader open until the user confirms
         * NOTE: This only work if the loader is shown as a modal
         * @default false
         */
        confirmClose?: boolean;

        /**
         * The delay before the spinner starts
         * @default 500
         */
        delay?: number;

        type?: 'spinner' | 'table';
    }>(),
    {
        label: '',
        state: null,
        showAsModal: false,
        hideDetails: false,
        confirmClose: false,
        delay: 500,
        type: 'spinner',
    },
);

const emit = defineEmits<{
    (e: 'closed'): void;
}>();

const displayLabel = ref<string>(props.label);

// The current state of the loader
const state = computed<TaskLoaderState | null>(() => props.state);

// Start the spinner after a delay
setTimeout(() => {
    starting.value = true;
}, props.delay);

/**
 * Get a random color for the spinner
 */
const getColor = () => {
    let colors = ['text-primary', 'text-success', 'text-danger', 'text-warning', 'text-info'];
    let max = colors.length - 1;
    let min = 0;
    return colors[Math.floor(Math.random() * (max - min)) + min];
};

const spinnerColor = getColor();

// Check if the details are open or not
const detailsOpen = ref<boolean>(false);

// Check if the language is loaded
const langLoaded = computed(() => props.langNotLoaded == null || !props.langNotLoaded);

/**
 * Set the label of the loader
 * @param label
 */
const setLabel = (label: string) => {
    displayLabel.value = label;
};

/**
 * Check if the modal has been closed. This is used to prevent the modal from showing again after it has been closed. This is only used if confirmClose is true.
 */
const closedModal = ref<boolean>(false);

/**
 * The size of the modal. This can be set by the parent component using the setModalSize function. If this is not set, the modal will have the default size.
 */
const modalSize = ref<'sm' | 'md' | 'lg' | 'xl' | null>(null);

/**
 * Set the size of the modal. This can be used by the parent component to set the size of the modal. If this is not set, the modal will have the default size.
 * @param size
 */
const setModalSize = (size?: 'sm' | 'md' | 'lg' | 'xl' | null) => {
    modalSize.value = size ?? null;
};

defineExpose({ setLabel, setModalSize });

let LoaderStatusUnsubscribeFunction: (() => Promise<void>) | null = null;

/**
 * Handle status messages from the backend
 * @param status
 */
const handleStatusUpdate = (status: StatusLoaderStatus[]) => {
    if (status == null || status.length === 0) {
        return;
    }

    const item = status[0];

    let statusText = (item.Status ?? '') != '' ? item.Status : 'loadingcomp.statustext';

    let statusValues = { current: item.Current, total: item.Total, elapsedTime: '', timeRemaining: '', failed: item.Failed };

    // is running
    if (item.Current > 0) {
        if (item.Total == item.Current) {
            // done
            statusValues.timeRemaining = $td(secondsToJsDate(0), 'TIME');
            statusValues.failed = item.Failed;
            statusValues.total = item.Total - item.Failed;
        } else {
            let diffRows = item.Total - item.Current;
            let interval = diffRows > 1000 ? 100 : diffRows > 100 ? 10 : 1;

            statusValues.current = Math.floor(item.Current / interval) * interval;
            statusValues.timeRemaining = $td(secondsToJsDate(((item.AverageTimePrItem ?? 0) * (item.Total - item.Current)) / 1000), 'TIME');
        }
        setLabel($ti(statusText!, statusValues, true));
    } else {
        setLabel(''); // Dont show anything
    }
};

/**
 * Help toggle the details open state
 */
const onDetailsOpen = () => {
    nextTick(() => {
        detailsOpen.value = !detailsOpen.value;
    });
};

const onModalClose = () => {
    closedModal.value = true;
    setLabel('');

    emit('closed');
    nextTick(() => {
        closedModal.value = false;
    });
};

/**
 * We set up a subscription to status messages. This uses the User ID to filter the messages. This mean that there can only be one process that sends status messages for a user at a time.
 * This should not be an issue since the loader is only used for long running tasks that are started by the user.
 */
onMounted(async () => {
    if (!user.value?.ID) {
        console.warn('User ID is not set, cannot subscribe to status updates');
        return;
    }

    if (LoaderStatusUnsubscribeFunction) {
        await LoaderStatusUnsubscribeFunction();
    }
    console.log('subscribing to status updates for user ID:', user.value?.ID);
    LoaderStatusUnsubscribeFunction = await signalrSubscribe.StatusLoaderStatus({ UserID: user.value?.ID! }, handleStatusUpdate);
});

/**
 * Unsubscribe from the status messages when the component is unmounted.
 */
onUnmounted(async () => {
    if (!user.value?.ID) {
        console.warn('User ID is not set, no status updates to unsubscribe from');
        return;
    }

    if (LoaderStatusUnsubscribeFunction) {
        await LoaderStatusUnsubscribeFunction();
    }

    console.log('Unsubscribing from status updates for user ID:', user.value?.ID);
    LoaderStatusUnsubscribeFunction = null;
});
</script>
<template>
    <div
        class="skeleton-card w-100"
        v-if="
            type == 'table' &&
            state != null &&
            ((showAsModal && confirmClose && !closedModal) || !confirmClose) &&
            ((starting && state.isActive) || state.Failed.length > 0)
        "
    >
        <!-- <div class="skeleton-image"></div> -->
        <div class="skeleton-content">
            <div class="skeleton-line"></div>
            <div class="skeleton-line"></div>
            <div class="skeleton-line"></div>
            <div class="skeleton-line"></div>
            <div class="skeleton-line"></div>
            <div class="skeleton-line"></div>
        </div>
    </div>
    <div
        v-else-if="
            (state != null && showAsModal && confirmClose && !closedModal) ||
            (state != null &&
                ((showAsModal && confirmClose && !closedModal) || !confirmClose) &&
                ((starting && state.isActive) || state.Failed.length > 0))
        "
        :class="{ 'eq-modal': showAsModal, ['modal-' + modalSize]: modalSize != null }"
    >
        <div class="d-flex justify-content-center mb-3">
            <div
                v-if="(showAsModal && confirmClose) || !state.AllDone || state.Failed.length > 0"
                class="d-flex flex-column flex-wrap align-items-center"
            >
                <IconComp
                    v-if="state.AllDone && state.Failed.length == 0"
                    :icon="featureIcons.SUCCESS"
                    class="text-success mb-2"
                    size="3x"
                />
                <IconComp
                    v-else-if="state.Failed.length == 0"
                    icon="fa-spinner-third"
                    :class="['fa-spin', spinnerColor, 'mb-2']"
                    size="3x"
                />
                <IconComp
                    v-else
                    :icon="featureIcons.ERROR"
                    class="text-danger mb-2"
                    size="3x"
                ></IconComp>

                <slot></slot>

                <div
                    v-if="displayLabel != ''"
                    v-html="displayLabel"
                    class="text-center mt-2"
                ></div>

                <div v-if="!hideDetails && !showAsModal && state.NeedsLoading.length > 1">
                    {{ state.Loaded.length }} / {{ state.NeedsLoading.length }}
                    <HelpIcon
                        class="opacity-hover-100"
                        @click="onDetailsOpen"
                    />
                </div>

                <ButtonComp
                    v-if="showAsModal && confirmClose"
                    button-type="Primary"
                    aria-disabled="true"
                    :disabled="!state.AllDone"
                    @click="onModalClose"
                    >{{ $tm('button.close') }}</ButtonComp
                >
            </div>
        </div>
        <CollapseContent :visible="detailsOpen">
            <div class="d-flex justify-content-center">
                <div>
                    <h5>{{ $tm('general.currentlyloading') }}</h5>
                    <ul class="list-unstyled ms-3">
                        <li
                            v-for="l in state.NeedsLoading"
                            :key="l.key"
                            class="d-flex"
                        >
                            <IconComp
                                :icon="!l.done ? 'fa-spinner-third' : l.error != null ? 'fa-circle-exclamation' : 'fa-check'"
                                class="me-3 fa-fw"
                                :class="{
                                    'fa-spin': !l.done,
                                    'fa-fw': true,
                                    'text-success': l.done && l.error === null,
                                    'text-danger': l.done && l.error != null,
                                }"
                            />
                            <template v-if="langLoaded">{{ $tm(l.key) }}</template>
                        </li>
                    </ul>
                </div>
            </div>
            <div
                v-for="l in state.Failed"
                :key="l.key"
            >
                <div v-if="l.error != null">
                    <template v-if="langLoaded">
                        <div
                            class="text-truncate-container"
                            role="alert"
                        >
                            <pre class="alert alert-danger mx-2">{{ $tm(l.error) }}</pre>
                        </div>
                    </template>
                </div>
            </div>
        </CollapseContent>
    </div>
</template>
